import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import SpeechProfileCard from './SpeechProfileCard';
import {
  GraduationCap,
  Briefcase,
  Search,
  Users,
  Mic,
  Video,
  Trophy,
  Target,
  ArrowRight,
  CheckCircle2,
  Clock,
  Lock,
  Loader2,
  TrendingUp,
  Award,
  Flame,
  Star,
  Zap,
  ChevronRight,
  BarChart3,
  BookOpen,
  Code,
  Brain,
  Sparkles,
  MessageSquare,
  Crown
} from 'lucide-react';

// Phase status configurations
const statusConfig = {
  completed: { label: 'Completed', color: 'bg-emerald-500', textColor: 'text-emerald-500', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', textColor: 'text-blue-500', icon: Clock },
  not_started: { label: 'Not Started', color: 'bg-slate-400', textColor: 'text-slate-400', icon: Clock },
  locked: { label: 'Locked', color: 'bg-slate-300', textColor: 'text-slate-400', icon: Lock },
  needs_improvement: { label: 'Needs Improvement', color: 'bg-amber-500', textColor: 'text-amber-500', icon: Target }
};

// R-PAT phases configuration
const rpatPhases = [
  { id: 1, name: 'Foundation', description: 'Core concepts & training basics' },
  { id: 2, name: 'Skills', description: 'Industry-specific skills' },
  { id: 3, name: 'Practice', description: 'Simulated exercises' },
  { id: 4, name: 'Assessment', description: 'Knowledge verification' },
  { id: 5, name: 'Job Mastery', description: 'Industry job simulations' },
  { id: 6, name: 'Sourcing', description: 'Candidate sourcing labs' },
  { id: 7, name: 'Role Play', description: 'Communication scenarios' },
  { id: 8, name: 'Certification', description: 'Final assessment' }
];

const CareerJourneyDashboard = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [rpatStatus, setRpatStatus] = useState(null);
  const [gamificationData, setGamificationData] = useState(null);

  // Get feature access from user - defaults to true if not specified
  const featureAccess = user?.feature_access || {};
  const hasStaffingAccess = featureAccess.staffing_training !== false;
  const hasSimulationsAccess = featureAccess.simulations !== false;
  const hasJobSimulationsAccess = featureAccess.job_simulations !== false;
  const hasSourcingLabsAccess = featureAccess.sourcing_labs !== false;
  const hasEvolveAccess = featureAccess.evolve !== false;
  const hasInsideSalesAccess = featureAccess.inside_sales_training !== false;
  const hasTechLearningAccess = featureAccess.tech_learning !== false;
  const hasCommunicationAccess = featureAccess.communication_training !== false;
  const hasLeadershipAccess = featureAccess.leadership_training !== false;

  // Calculate if user has any staffing-related features (for showing R-PAT section)
  const hasAnyStaffingFeature = hasStaffingAccess || hasSimulationsAccess || hasJobSimulationsAccess || hasSourcingLabsAccess;

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, rpatRes, gamificationRes] = await Promise.all([
        api.get('/dashboard/user/comprehensive'),
        api.get('/rpat/certification/status').catch(() => ({ data: null })),
        api.get('/gamification/profile').catch(() => ({ data: null }))
      ]);
      
      setDashboardData(dashboardRes.data);
      setRpatStatus(rpatRes.data);
      setGamificationData(gamificationRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="career-journey-loading">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  const features = dashboardData?.features || {};
  const overallScores = dashboardData?.overall_scores || {};

  // Calculate overall career progress
  const calculateOverallProgress = () => {
    let totalScore = 0;
    let count = 0;
    
    if (features.rpat?.avg_score > 0) { totalScore += features.rpat.avg_score; count++; }
    if (features.simulations?.avg_score > 0) { totalScore += features.simulations.avg_score; count++; }
    if (features.job_simulations?.avg_score > 0) { totalScore += features.job_simulations.avg_score; count++; }
    if (features.sourcing_labs?.avg_score > 0) { totalScore += features.sourcing_labs.avg_score; count++; }
    if (features.evolve?.avg_score > 0) { totalScore += features.evolve.avg_score; count++; }
    
    return count > 0 ? Math.round(totalScore / count) : 0;
  };

  const overallProgress = calculateOverallProgress();

  // Get R-PAT phase status based on actual API response structure
  // API returns:
  // - phase_4_completed: boolean (indicates if phases 1-4 are done)
  // - phases.phase_5..8.status: 'locked' | 'available' | 'in_progress' | 'completed' | 'needs_improvement'
  // - is_certified: boolean
  const getPhaseStatus = (phaseNum) => {
    if (!rpatStatus) return 'not_started';
    
    // For phases 1-4, we check phase_4_completed for overall status
    // and rpat features data for individual phase progress
    if (phaseNum <= 4) {
      // If phase 4 is completed, all phases 1-4 are completed
      if (rpatStatus.phase_4_completed) {
        return 'completed';
      }
      // Otherwise, check if user has started R-PAT (phases 1-4 are all "in progress" until phase 4 is done)
      // Once phase 4 is completed, it means all phases 1-4 are done
      // If not completed but user has R-PAT data, show as in_progress
      if (features.rpat?.total_sessions > 0) {
        return 'in_progress';
      }
      return 'not_started';
    }
    
    // For phases 5-8, check the nested phases object
    const phaseKey = `phase_${phaseNum}`;
    const phaseData = rpatStatus.phases?.[phaseKey];
    
    if (!phaseData) {
      // If phase 4 is not completed, phases 5-8 are locked
      return rpatStatus.phase_4_completed ? 'not_started' : 'locked';
    }
    
    const status = phaseData.status;
    
    if (status === 'completed') return 'completed';
    if (status === 'in_progress' || status === 'available') return 'in_progress';
    if (status === 'needs_improvement') return 'needs_improvement';
    if (status === 'locked') return 'locked';
    return 'not_started';
  };

  // Get completed phases count
  const getCompletedPhasesCount = () => {
    if (!rpatStatus) return 0;
    let count = 0;
    
    // Check phases 1-4 (all completed if phase_4_completed is true)
    if (rpatStatus.phase_4_completed) {
      count += 4;
    }
    
    // Check phases 5-8 from nested phases object
    for (let i = 5; i <= 8; i++) {
      const phaseKey = `phase_${i}`;
      if (rpatStatus.phases?.[phaseKey]?.status === 'completed') {
        count++;
      }
    }
    
    return count;
  };

  // Check if user is R-PAT certified
  const isCertified = rpatStatus?.is_certified || rpatStatus?.phases?.phase_8?.certificate_id;

  return (
    <div className="space-y-6" data-testid="career-journey-dashboard">
      {/* Overall Progress Header */}
      <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-0 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <CardContent className="relative p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-violet-600 flex items-center justify-center shadow-lg shadow-primary/25">
                  <Trophy className="h-10 w-10 text-white" />
                </div>
                {gamificationData?.level && (
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center text-sm font-bold text-white shadow-lg">
                    {gamificationData.level.level}
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold">Career Journey</h2>
                <p className="text-slate-400 text-sm mt-1">
                  {user?.name}&apos;s training progress
                </p>
                {gamificationData?.level?.title && (
                  <Badge className="mt-2 bg-white/10 text-white border-white/20">
                    {gamificationData.level.title}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Overall Score Circle */}
              <div className="text-center">
                <div className="relative w-24 h-24">
                  <svg className="w-24 h-24 transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-white/10"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="url(#progressGradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${overallProgress * 2.51} 251`}
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold">{overallProgress}%</span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">Overall Score</p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="px-4 py-2 rounded-lg bg-white/5">
                  <p className="text-2xl font-bold text-emerald-400">{getCompletedPhasesCount()}/8</p>
                  <p className="text-xs text-slate-400">R-PAT Phases</p>
                </div>
                <div className="px-4 py-2 rounded-lg bg-white/5">
                  <p className="text-2xl font-bold text-blue-400">
                    {(features.simulations?.completed || 0) + (features.job_simulations?.completed || 0)}
                  </p>
                  <p className="text-xs text-slate-400">Simulations</p>
                </div>
                <div className="px-4 py-2 rounded-lg bg-white/5">
                  <p className="text-2xl font-bold text-purple-400">{features.sourcing_labs?.completed || 0}</p>
                  <p className="text-xs text-slate-400">Labs Done</p>
                </div>
                <div className="px-4 py-2 rounded-lg bg-white/5">
                  <p className="text-2xl font-bold text-orange-400">{gamificationData?.streak?.current_streak || 0}</p>
                  <p className="text-xs text-slate-400">Day Streak</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* R-PAT Certification Progress - Only show if user has any staffing-related feature */}
      {hasAnyStaffingFeature && (
      <Card data-testid="rpat-progress-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-violet-500/10">
                <GraduationCap className="h-6 w-6 text-violet-500" />
              </div>
              <div>
                <CardTitle className="text-lg">R-PAT Certification Path</CardTitle>
                <CardDescription>8-phase recruitment professional assessment</CardDescription>
              </div>
            </div>
            <Link to="/rpat">
              <Button variant="outline" size="sm" className="gap-2">
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {/* Phase Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Phase Progress</span>
              <span className="font-medium">{getCompletedPhasesCount()} of 8 completed</span>
            </div>
            <div className="flex gap-1">
              {rpatPhases.map((phase) => {
                const status = getPhaseStatus(phase.id);
                const config = statusConfig[status];
                return (
                  <div
                    key={phase.id}
                    className={`h-2 flex-1 rounded-full ${config.color} transition-all`}
                    title={`Phase ${phase.id}: ${phase.name} - ${config.label}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Phase Cards Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {rpatPhases.map((phase) => {
              const status = getPhaseStatus(phase.id);
              const config = statusConfig[status];
              const StatusIcon = config.icon;
              
              return (
                <Link 
                  key={phase.id} 
                  to={status !== 'locked' ? '/rpat' : '#'}
                  className={status === 'locked' ? 'pointer-events-none' : ''}
                >
                  <div 
                    className={`p-3 rounded-xl border text-center transition-all hover:shadow-md ${
                      status === 'completed' ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' :
                      status === 'in_progress' ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' :
                      status === 'needs_improvement' ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800' :
                      status === 'locked' ? 'bg-slate-50 border-slate-200 dark:bg-slate-900/20 dark:border-slate-700 opacity-50' :
                      'bg-card border-border hover:border-primary/50'
                    }`}
                    data-testid={`rpat-phase-${phase.id}`}
                  >
                    <div className={`mx-auto w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                      status === 'completed' ? 'bg-emerald-100 dark:bg-emerald-800' :
                      status === 'in_progress' ? 'bg-blue-100 dark:bg-blue-800' :
                      'bg-slate-100 dark:bg-slate-800'
                    }`}>
                      <StatusIcon className={`h-4 w-4 ${config.textColor}`} />
                    </div>
                    <p className="text-xs font-semibold">Phase {phase.id}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{phase.name}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Certificate Badge */}
          {isCertified && (
            <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-200 dark:border-yellow-800 flex items-center gap-4">
              <Award className="h-10 w-10 text-yellow-600" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-800 dark:text-yellow-200">R-PAT Certified!</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Certificate ID: {rpatStatus?.phases?.phase_8?.certificate_id || 'Certified'}
                </p>
              </div>
              <Link to="/rpat/certificate">
                <Button size="sm" variant="outline" className="border-yellow-300 text-yellow-700 hover:bg-yellow-100">
                  View Certificate
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Training Modules Grid */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Training Modules
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Recruiter Simulations Card */}
          {hasSimulationsAccess && (
            <ModuleCard
              title="Recruiter Simulations"
              description="Practice real-world recruitment scenarios"
              icon={Mic}
              iconBg="bg-blue-500/10"
              iconColor="text-blue-500"
              link="/recruiter-simulations-analytics"
              stats={features.simulations}
              primaryStat={{ label: 'Sessions', value: features.simulations?.total_sessions || 0 }}
              secondaryStat={{ label: 'Completed', value: features.simulations?.completed || 0 }}
              score={features.simulations?.avg_score || 0}
              enabled={features.simulations?.enabled !== false}
              testId="module-recruiter-sims"
            />
          )}

          {/* Job Simulations Card */}
          {hasJobSimulationsAccess && (
            <ModuleCard
              title="Job Simulations"
              description="Industry-specific job skill training"
              icon={Briefcase}
              iconBg="bg-purple-500/10"
              iconColor="text-purple-500"
              link="/job-simulations-analytics"
              stats={features.job_simulations}
              primaryStat={{ label: 'Sessions', value: features.job_simulations?.total_sessions || 0 }}
              secondaryStat={{ label: 'Completed', value: features.job_simulations?.completed || 0 }}
              score={features.job_simulations?.avg_score || 0}
              enabled={features.job_simulations?.enabled !== false}
              testId="module-job-sims"
            />
          )}

          {/* Sourcing Labs Card */}
          {hasSourcingLabsAccess && (
            <ModuleCard
              title="Sourcing Labs"
              description="Master candidate sourcing techniques"
              icon={Search}
              iconBg="bg-emerald-500/10"
              iconColor="text-emerald-500"
              link="/sourcing-labs-analytics"
              stats={features.sourcing_labs}
              primaryStat={{ label: 'Sessions', value: features.sourcing_labs?.total_sessions || 0 }}
              secondaryStat={{ label: 'Completed', value: features.sourcing_labs?.completed || 0 }}
              score={features.sourcing_labs?.avg_score || 0}
              enabled={features.sourcing_labs?.enabled !== false}
              testId="module-sourcing-labs"
            />
          )}

          {/* Role Play Card - Uses R-PAT Phase 7 data */}
          {hasAnyStaffingFeature && (
            <ModuleCard
              title="Role Play Sessions"
              description="Communication & interview scenarios"
              icon={Users}
              iconBg="bg-orange-500/10"
              iconColor="text-orange-500"
              link="/roleplay"
              stats={{}}
              primaryStat={{ 
                label: 'Scenarios', 
                value: `${rpatStatus?.phases?.phase_7?.completed_scenarios || 0}/${rpatStatus?.phases?.phase_7?.total_scenarios || 12}` 
              }}
              secondaryStat={{ 
                label: 'Categories', 
                value: `${rpatStatus?.phases?.phase_7?.categories_complete || 0}/${rpatStatus?.phases?.phase_7?.total_categories || 6}` 
              }}
              score={rpatStatus?.phases?.phase_7?.average_score || 0}
              enabled={true}
              testId="module-roleplay"
            />
          )}

          {/* Evolve AI Card */}
          {hasEvolveAccess && (
            <ModuleCard
              title="Evolve AI"
              description="AI-powered presentation coaching"
              icon={Video}
              iconBg="bg-pink-500/10"
              iconColor="text-pink-500"
              link="/evolve-analytics"
              stats={features.evolve}
              primaryStat={{ label: 'Sessions', value: features.evolve?.total_sessions || 0 }}
              secondaryStat={{ label: 'Completed', value: features.evolve?.completed || 0 }}
              score={features.evolve?.avg_score || 0}
              enabled={features.evolve?.enabled !== false}
              testId="module-evolve"
            />
          )}

          {/* Inside Sales Training Card */}
          {hasInsideSalesAccess && (
            <ModuleCard
              title="Inside Sales Training"
              description="B2B sales mastery program"
              icon={TrendingUp}
              iconBg="bg-indigo-500/10"
              iconColor="text-indigo-500"
              link="/inside-sales"
              stats={features.inside_sales_training}
              primaryStat={{ label: 'Progress', value: `${features.inside_sales_training?.completed || 0}/20` }}
              secondaryStat={{ label: 'Completion', value: `${features.inside_sales_training?.completion_rate || 0}%` }}
              score={features.inside_sales_training?.avg_score || 0}
              enabled={features.inside_sales_training?.enabled !== false}
              testId="module-inside-sales"
              showCertified={features.inside_sales_training?.is_certified}
            />
          )}

          {/* Tech Learning Card */}
          {hasTechLearningAccess && (
            <ModuleCard
              title="Tech Learning"
              description="IT courses & technical skills"
              icon={Code}
              iconBg="bg-cyan-500/10"
              iconColor="text-cyan-500"
              link="/tech-learning"
              stats={features.tech_learning}
              primaryStat={{ label: 'Courses', value: features.tech_learning?.enrolled_courses || 0 }}
              secondaryStat={{ label: 'Completed', value: features.tech_learning?.completed_courses || 0 }}
              score={features.tech_learning?.avg_score || 0}
              enabled={features.tech_learning?.enabled !== false}
              testId="module-tech-learning"
            />
          )}

          {/* Leadership Training Card */}
          {hasLeadershipAccess && (
            <ModuleCard
              title="Leadership Training"
              description="Management & leadership skills"
              icon={Crown}
              iconBg="bg-amber-500/10"
              iconColor="text-amber-500"
              link="/leadership"
              stats={features.leadership_training}
              primaryStat={{ label: 'Modules', value: features.leadership_training?.completed || 0 }}
              secondaryStat={{ label: 'Progress', value: `${features.leadership_training?.completion_rate || 0}%` }}
              score={features.leadership_training?.avg_score || 0}
              enabled={false}
              testId="module-leadership"
            />
          )}

          {/* Communication Coach Card */}
          {hasCommunicationAccess && (
            <ModuleCard
              title="Communication Coach"
              description="Professional communication skills"
              icon={MessageSquare}
              iconBg="bg-rose-500/10"
              iconColor="text-rose-500"
              link="/communication"
              stats={features.communication_training}
              primaryStat={{ label: 'Sessions', value: features.communication_training?.total_sessions || 0 }}
              secondaryStat={{ label: 'Skills', value: features.communication_training?.skills_mastered || 0 }}
              score={features.communication_training?.avg_score || 0}
              enabled={false}
              testId="module-communication"
            />
          )}
        </div>
      </div>

      {/* Voice Profile Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Mic className="h-5 w-5 text-violet-500" />
          Voice Analysis
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SpeechProfileCard />
          
          {/* Quick Voice Tips Card */}
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
            <CardContent className="p-5">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" />
                Quick Voice Tips
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Speak at 120-160 words per minute for optimal clarity</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Pause instead of using filler words like &quot;um&quot; or &quot;uh&quot;</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Vary your vocabulary to keep listeners engaged</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Record yourself and listen back for improvements</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Performance Overview */}
      <Card data-testid="performance-overview">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Performance Overview</CardTitle>
              <CardDescription>Your skill scores across all training areas</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ScoreCard label="Voice Score" value={overallScores.voice || 0} color="from-blue-500 to-cyan-500" />
            <ScoreCard label="Communication" value={overallScores.communication || 0} color="from-purple-500 to-pink-500" />
            <ScoreCard label="Assessment" value={overallScores.assessment || 0} color="from-emerald-500 to-teal-500" />
            <ScoreCard label="Simulation" value={overallScores.simulation || 0} color="from-orange-500 to-amber-500" />
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickActionCard
          title="Resume Training"
          description="Pick up where you left off"
          icon={Zap}
          link="/training"
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          testId="quick-action-training"
        />
        <QuickActionCard
          title="Start Simulation"
          description="Practice real scenarios"
          icon={Mic}
          link="/simulation"
          color="bg-gradient-to-br from-purple-500 to-purple-600"
          testId="quick-action-simulation"
        />
        <QuickActionCard
          title="Leaderboard"
          description="See how you rank"
          icon={Trophy}
          link="/leaderboard"
          color="bg-gradient-to-br from-amber-500 to-orange-500"
          testId="quick-action-leaderboard"
        />
        <QuickActionCard
          title="View Badges"
          description="Check your achievements"
          icon={Award}
          link="/badges"
          color="bg-gradient-to-br from-emerald-500 to-teal-500"
          testId="quick-action-badges"
        />
      </div>
    </div>
  );
};

// Module Card Component
const ModuleCard = ({ 
  title, 
  description, 
  icon: Icon, 
  iconBg, 
  iconColor, 
  link, 
  stats, 
  primaryStat, 
  secondaryStat, 
  score, 
  enabled = true,
  testId,
  showCertified = false
}) => {
  if (!enabled) {
    return (
      <Card className="opacity-50 cursor-not-allowed" data-testid={`${testId}-locked`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className={`p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800`}>
              <Icon className="h-5 w-5 text-slate-400" />
            </div>
            <Lock className="h-5 w-5 text-slate-400" />
          </div>
          <h4 className="font-semibold text-slate-500">{title}</h4>
          <p className="text-xs text-slate-400 mt-1">{description}</p>
          <p className="text-xs text-slate-400 mt-4">Contact admin for access</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Link to={link} data-testid={testId}>
      <Card className="h-full hover:shadow-lg hover:border-primary/30 transition-all group cursor-pointer">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className={`p-2.5 rounded-xl ${iconBg}`}>
              <Icon className={`h-5 w-5 ${iconColor}`} />
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          
          <h4 className="font-semibold group-hover:text-primary transition-colors">{title}</h4>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{description}</p>
          
          {/* Stats Row */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="p-2 rounded-lg bg-muted/50 text-center">
              <p className="text-lg font-bold">{primaryStat.value}</p>
              <p className="text-[10px] text-muted-foreground">{primaryStat.label}</p>
            </div>
            <div className="p-2 rounded-lg bg-muted/50 text-center">
              <p className="text-lg font-bold">{secondaryStat.value}</p>
              <p className="text-[10px] text-muted-foreground">{secondaryStat.label}</p>
            </div>
          </div>
          
          {/* Score Bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Avg Score</span>
              <span className={`font-semibold ${score >= 80 ? 'text-emerald-500' : score >= 60 ? 'text-amber-500' : 'text-slate-500'}`}>
                {score}%
              </span>
            </div>
            <Progress value={score} className="h-1.5" />
          </div>

          {/* Certified Badge */}
          {showCertified && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-600">
              <Award className="h-3.5 w-3.5" />
              <span className="font-medium">Certified</span>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

// Score Card Component
const ScoreCard = ({ label, value, color }) => {
  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border">
      <p className="text-xs text-muted-foreground mb-2">{label}</p>
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
          <span className="text-lg font-bold text-white">{value}</span>
        </div>
        <div className="flex-1">
          <Progress value={value} className="h-2" />
        </div>
      </div>
    </div>
  );
};

// Quick Action Card Component
const QuickActionCard = ({ title, description, icon: Icon, link, color, testId }) => {
  return (
    <Link to={link} data-testid={testId}>
      <Card className={`${color} text-white border-0 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer`}>
        <CardContent className="p-4">
          <Icon className="h-6 w-6 mb-2 opacity-90" />
          <h4 className="font-semibold text-sm">{title}</h4>
          <p className="text-xs opacity-80">{description}</p>
        </CardContent>
      </Card>
    </Link>
  );
};

export default CareerJourneyDashboard;
